export function bouncer() {
    const image = document.querySelector('[data-bouncer]');
    if (!image) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = window.matchMedia('(max-width: 767px)');
    const speed = 225;
    // Keep the spacing between copies equal on mobile and desktop.
    const trailDelay = 2.56 / 24;
    let trailCount = 0;
    const trail = [];
    const history = [];
    let elapsedTime = 0;
    let x = 0;
    let y = 0;
    let velocityX = 0;
    let velocityY = 0;
    let maxX = 0;
    let maxY = 0;
    let frame = null;
    let previousTime = null;
    let active = false;

    const clearTrail = () => {
        trail.forEach((copy) => copy.remove());
        trail.length = 0;
        history.length = 0;
        elapsedTime = 0;
    };

    const createTrail = () => {
        trailCount = mobile.matches ? 12 : 24;
        const fragment = document.createDocumentFragment();

        // Oldest copies sit underneath the newest copies and the original image.
        for (let index = trailCount - 1; index >= 0; index--) {
            const copy = image.cloneNode(false);
            copy.removeAttribute('id');
            copy.removeAttribute('data-bouncer');
            copy.setAttribute('alt', '');
            copy.setAttribute('aria-hidden', 'true');
            copy.classList.add('error-page__image--trail');
            copy.style.opacity = '0';
            trail[index] = copy;
            fragment.appendChild(copy);
        }

        image.before(fragment);
    };

    const renderTrail = () => {
        history.push({ x, y, time: elapsedTime });
        const oldestTime = elapsedTime - trailCount * trailDelay;
        while (history.length > 2 && history[1].time < oldestTime) history.shift();

        let cursor = history.length - 1;
        trail.forEach((copy, index) => {
            const targetTime = elapsedTime - (index + 1) * trailDelay;
            if (targetTime < history[0].time) {
                copy.style.opacity = '0';
                return;
            }

            while (cursor > 0 && history[cursor].time > targetTime) cursor--;
            const from = history[cursor];
            const to = history[Math.min(cursor + 1, history.length - 1)];
            const fraction = to.time === from.time ? 0 : (targetTime - from.time) / (to.time - from.time);
            const trailX = from.x + (to.x - from.x) * fraction;
            const trailY = from.y + (to.y - from.y) * fraction;
            copy.style.transform = `translate3d(${trailX}px, ${trailY}px, 0)`;
            copy.style.opacity = String(Math.pow(1 - (index + 1) / (trailCount + 1), 1.5) * 0.85);
        });
    };

    const render = () => {
        image.style.transform = `translate3d(${x}px, ${y}px, 0)`;
    };

    const measure = () => {
        if (!active) return;

        const bounds = image.getBoundingClientRect();
        maxX = Math.max(0, document.documentElement.clientWidth - bounds.width);
        maxY = Math.max(0, window.innerHeight - bounds.height);
        x = Math.min(maxX, Math.max(0, x));
        y = Math.min(maxY, Math.max(0, y));
        // Discard the old path when the viewport or image dimensions change.
        history.length = 0;
        trail.forEach((copy) => { copy.style.opacity = '0'; });
        render();
    };

    const animate = (time) => {
        if (!active) return;

        // Cap elapsed time so returning to a background tab never causes a jump.
        const elapsed = previousTime === null ? 0 : Math.min((time - previousTime) / 1000, 0.05);
        previousTime = time;
        elapsedTime += elapsed;
        x += velocityX * elapsed;
        y += velocityY * elapsed;

        if (x <= 0 || x >= maxX) {
            velocityX = x <= 0 ? Math.abs(velocityX) : -Math.abs(velocityX);
            x = Math.min(maxX, Math.max(0, x));
        }
        if (y <= 0 || y >= maxY) {
            velocityY = y <= 0 ? Math.abs(velocityY) : -Math.abs(velocityY);
            y = Math.min(maxY, Math.max(0, y));
        }

        render();
        if (elapsed > 0) renderTrail();
        frame = requestAnimationFrame(animate);
    };

    const updateMotion = () => {
        if (frame !== null) cancelAnimationFrame(frame);
        frame = null;
        previousTime = null;
        clearTrail();
        active = !reducedMotion.matches;
        image.classList.toggle('is-bouncing', active);

        if (!active) {
            image.style.removeProperty('transform');
            return;
        }

        measure();
        x = Math.random() * maxX;
        y = Math.random() * maxY;
        // Keep both axes moving, with a random angle and quadrant.
        const angle = (20 + Math.random() * 50) * Math.PI / 180;
        velocityX = Math.cos(angle) * speed * (Math.random() < 0.5 ? -1 : 1);
        velocityY = Math.sin(angle) * speed * (Math.random() < 0.5 ? -1 : 1);
        render();
        createTrail();
        frame = requestAnimationFrame(animate);
    };

    window.addEventListener('resize', measure);
    image.addEventListener('load', measure);
    reducedMotion.addEventListener('change', updateMotion);
    const updateMobile = () => {
        if (!active) return;
        clearTrail();
        createTrail();
    };
    mobile.addEventListener('change', updateMobile);
    const observer = new ResizeObserver(measure);
    observer.observe(image);
    updateMotion();
    return () => {
        active = false;
        cancelAnimationFrame(frame);
        clearTrail();
        observer.disconnect();
        window.removeEventListener('resize', measure);
        image.removeEventListener('load', measure);
        reducedMotion.removeEventListener('change', updateMotion);
        mobile.removeEventListener('change', updateMobile);
    };
}
